import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "@/api/client";
import { clientsService, commandesService, produitsService } from "@/api/services";
import { ErrorState } from "@/components/common/ErrorState";
import { LoadingState } from "@/components/common/LoadingState";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { useI18n } from "@/i18n/I18nProvider";
import type {
  Client,
  CommandeCreatePayload,
  CommandeCreateResponse,
  Product,
} from "@/types/domain";

interface FormLine {
  key: number;
  sku: string;
  quantite: string;
  prixUnitairePo: string;
}

interface FormErrors {
  clientId?: string;
  numeroPoClient?: string;
  lignes?: string;
  lineErrors: Record<number, Partial<Record<"sku" | "quantite" | "prixUnitairePo", string>>>;
}

const inputClassName =
  "ops-focus w-full rounded-sm border border-border bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60";

const emptyErrors = (): FormErrors => ({ lineErrors: {} });

export default function NouvelleCommandePage() {
  const { t } = useI18n();
  const nextLineKey = useRef(2);
  const [clients, setClients] = useState<Client[]>([]);
  const [produits, setProduits] = useState<Product[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [produitsLoading, setProduitsLoading] = useState(true);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [produitsError, setProduitsError] = useState<string | null>(null);

  const [clientId, setClientId] = useState("");
  const [numeroPoClient, setNumeroPoClient] = useState("");
  const [courrielAcheteur, setCourrielAcheteur] = useState("");
  const [lignes, setLignes] = useState<FormLine[]>([
    { key: 1, sku: "", quantite: "", prixUnitairePo: "" },
  ]);
  const [errors, setErrors] = useState<FormErrors>(emptyErrors);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdCommande, setCreatedCommande] = useState<CommandeCreateResponse | null>(null);

  const loadClients = useCallback(async () => {
    try {
      setClientsLoading(true);
      setClientsError(null);
      const data = await clientsService.list();
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur chargement clients pour la commande :", err);
      setClients([]);
      setClientsError("Impossible de charger les clients.");
    } finally {
      setClientsLoading(false);
    }
  }, []);

  const loadProduits = useCallback(async () => {
    try {
      setProduitsLoading(true);
      setProduitsError(null);
      const data = await produitsService.list();
      setProduits(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur chargement produits pour la commande :", err);
      setProduits([]);
      setProduitsError("Impossible de charger les produits.");
    } finally {
      setProduitsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
    loadProduits();
  }, [loadClients, loadProduits]);

  const addLine = () => {
    setLignes((current) => [
      ...current,
      {
        key: nextLineKey.current++,
        sku: "",
        quantite: "",
        prixUnitairePo: "",
      },
    ]);
    setErrors((current) => ({ ...current, lignes: undefined }));
  };

  const removeLine = (key: number) => {
    setLignes((current) => current.filter((ligne) => ligne.key !== key));
    setErrors((current) => {
      const { [key]: _removed, ...remainingLineErrors } = current.lineErrors;
      return { ...current, lineErrors: remainingLineErrors };
    });
  };

  const updateLine = (key: number, field: keyof Omit<FormLine, "key">, value: string) => {
    setLignes((current) =>
      current.map((ligne) => (ligne.key === key ? { ...ligne, [field]: value } : ligne)),
    );
    setErrors((current) => ({
      ...current,
      lineErrors: {
        ...current.lineErrors,
        [key]: { ...current.lineErrors[key], [field]: undefined },
      },
    }));
  };

  const validate = (): FormErrors => {
    const nextErrors = emptyErrors();

    if (!clientId) nextErrors.clientId = "Sélectionnez un client.";
    if (!numeroPoClient.trim()) {
      nextErrors.numeroPoClient = "Le numéro de PO client est obligatoire.";
    }
    if (lignes.length === 0) {
      nextErrors.lignes = "Ajoutez au moins une ligne de commande.";
    }

    lignes.forEach((ligne) => {
      const lineError: FormErrors["lineErrors"][number] = {};
      const quantite = Number(ligne.quantite);
      const prix = Number(ligne.prixUnitairePo);

      if (!ligne.sku) lineError.sku = "Sélectionnez un produit.";
      if (
        ligne.quantite.trim() === "" ||
        !Number.isInteger(quantite) ||
        quantite <= 0
      ) {
        lineError.quantite = "Entrez un entier supérieur à 0.";
      }
      if (
        ligne.prixUnitairePo.trim() === "" ||
        !Number.isFinite(prix) ||
        prix < 0
      ) {
        lineError.prixUnitairePo = "Entrez un prix supérieur ou égal à 0.";
      }

      if (Object.keys(lineError).length > 0) {
        nextErrors.lineErrors[ligne.key] = lineError;
      }
    });

    return nextErrors;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    setSubmitError(null);
    setCreatedCommande(null);

    if (
      nextErrors.clientId ||
      nextErrors.numeroPoClient ||
      nextErrors.lignes ||
      Object.keys(nextErrors.lineErrors).length > 0
    ) {
      return;
    }

    const payload: CommandeCreatePayload = {
      client_id: clientId,
      numero_po_client: numeroPoClient.trim(),
      courriel_acheteur: courrielAcheteur.trim() || null,
      lignes: lignes.map((ligne) => ({
        sku: ligne.sku,
        quantite: Number(ligne.quantite),
        prix_unitaire_po: Number(ligne.prixUnitairePo),
      })),
    };

    try {
      setSubmitting(true);
      const response = await commandesService.create(payload);
      setCreatedCommande(response);
    } catch (err) {
      console.error("Erreur création commande :", err);
      setSubmitError(
        err instanceof ApiError && err.status
          ? `La commande n’a pas pu être créée (HTTP ${err.status}).`
          : "La commande n’a pas pu être créée. Réessayez.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sortedClients = [...clients].sort((a, b) =>
    a.nom_legal.localeCompare(b.nom_legal, "fr"),
  );
  const sortedProduits = [...produits].sort((a, b) => a.sku.localeCompare(b.sku, "fr"));
  const referencesLoading = clientsLoading || produitsLoading;
  const formUnavailable = clientsLoading || produitsLoading || Boolean(clientsError || produitsError);

  return (
    <div className="space-y-6" data-testid="nouvelle-commande-page">
      <Link
        to="/commandes"
        className="ops-focus inline-flex rounded-sm text-sm font-medium text-muted-foreground hover:text-foreground hover:underline"
      >
        ← Retour aux commandes
      </Link>

      <PageHeader
        title="Nouvelle commande"
        subtitle="Saisissez les informations du PO client et ses lignes de produits."
        breadcrumb={[t.appSuffix, t.modules.commandes.title, "Nouvelle commande"]}
      />

      {referencesLoading && (
        <section className="ops-panel">
          <LoadingState label="Chargement des clients et produits..." rows={3} />
        </section>
      )}

      {clientsError && (
        <ErrorState
          title="Clients indisponibles"
          description={clientsError}
          retryLabel="Réessayer"
          onRetry={loadClients}
        />
      )}

      {produitsError && (
        <ErrorState
          title="Produits indisponibles"
          description={produitsError}
          retryLabel="Réessayer"
          onRetry={loadProduits}
        />
      )}

      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        <section className="ops-panel p-5">
          <div className="mb-5">
            <h2 className="text-lg font-semibold">Informations générales</h2>
            <p className="text-sm text-muted-foreground">Les champs marqués * sont obligatoires.</p>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <label className="space-y-1.5">
              <span className="ops-label">Client *</span>
              <select
                value={clientId}
                onChange={(event) => {
                  setClientId(event.target.value);
                  setErrors((current) => ({ ...current, clientId: undefined }));
                }}
                disabled={clientsLoading || Boolean(clientsError)}
                className={inputClassName}
                aria-invalid={Boolean(errors.clientId)}
              >
                <option value="">Sélectionner un client</option>
                {sortedClients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.nom_commercial || client.nom_legal} — {client.code_client}
                  </option>
                ))}
              </select>
              {errors.clientId && <p className="text-xs text-destructive">{errors.clientId}</p>}
            </label>

            <label className="space-y-1.5">
              <span className="ops-label">N° PO client *</span>
              <input
                value={numeroPoClient}
                onChange={(event) => {
                  setNumeroPoClient(event.target.value);
                  setErrors((current) => ({ ...current, numeroPoClient: undefined }));
                }}
                className={inputClassName}
                aria-invalid={Boolean(errors.numeroPoClient)}
                disabled={formUnavailable}
              />
              {errors.numeroPoClient && (
                <p className="text-xs text-destructive">{errors.numeroPoClient}</p>
              )}
            </label>

            <label className="space-y-1.5 md:col-span-2">
              <span className="ops-label">Courriel acheteur</span>
              <input
                type="email"
                value={courrielAcheteur}
                onChange={(event) => setCourrielAcheteur(event.target.value)}
                className={inputClassName}
                disabled={formUnavailable}
                placeholder="acheteur@entreprise.ca"
              />
            </label>
          </div>
        </section>

        <section className="ops-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold tracking-tight">Lignes de commande</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Sélectionnez le produit afin d’envoyer son SKU exact.
              </p>
            </div>
            <button
              type="button"
              onClick={addLine}
              disabled={formUnavailable || submitting}
              className="ops-focus rounded-sm border border-border bg-card px-3 py-2 text-sm font-semibold transition-colors hover:bg-[hsl(var(--surface-raised))] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Ajouter une ligne
            </button>
          </div>

          <div className="space-y-4 p-4">
            {errors.lignes && <p className="text-sm text-destructive">{errors.lignes}</p>}
            {lignes.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Aucune ligne. Ajoutez au moins un produit.
              </p>
            ) : (
              lignes.map((ligne, index) => {
                const lineError = errors.lineErrors[ligne.key] ?? {};
                return (
                  <fieldset
                    key={ligne.key}
                    disabled={formUnavailable || submitting}
                    className="grid gap-4 rounded-sm border border-border p-4 lg:grid-cols-[minmax(16rem,1fr)_10rem_12rem_auto] lg:items-start"
                  >
                    <legend className="px-1 text-xs font-semibold text-muted-foreground">
                      Ligne {index + 1}
                    </legend>

                    <label className="space-y-1.5">
                      <span className="ops-label">Produit / SKU *</span>
                      <select
                        value={ligne.sku}
                        onChange={(event) => updateLine(ligne.key, "sku", event.target.value)}
                        className={inputClassName}
                        aria-invalid={Boolean(lineError.sku)}
                      >
                        <option value="">Sélectionner un produit</option>
                        {sortedProduits.map((produit) => (
                          <option key={produit.id} value={produit.sku}>
                            {produit.sku} — {produit.description_officielle}
                          </option>
                        ))}
                      </select>
                      {lineError.sku && <p className="text-xs text-destructive">{lineError.sku}</p>}
                    </label>

                    <label className="space-y-1.5">
                      <span className="ops-label">Quantité *</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        inputMode="numeric"
                        value={ligne.quantite}
                        onChange={(event) => updateLine(ligne.key, "quantite", event.target.value)}
                        className={inputClassName}
                        aria-invalid={Boolean(lineError.quantite)}
                      />
                      {lineError.quantite && (
                        <p className="text-xs text-destructive">{lineError.quantite}</p>
                      )}
                    </label>

                    <label className="space-y-1.5">
                      <span className="ops-label">Prix unitaire PO *</span>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        inputMode="decimal"
                        value={ligne.prixUnitairePo}
                        onChange={(event) =>
                          updateLine(ligne.key, "prixUnitairePo", event.target.value)
                        }
                        className={inputClassName}
                        aria-invalid={Boolean(lineError.prixUnitairePo)}
                      />
                      {lineError.prixUnitairePo && (
                        <p className="text-xs text-destructive">{lineError.prixUnitairePo}</p>
                      )}
                    </label>

                    <button
                      type="button"
                      onClick={() => removeLine(ligne.key)}
                      className="ops-focus mt-5 rounded-sm border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
                    >
                      Supprimer
                    </button>
                  </fieldset>
                );
              })
            )}
          </div>
        </section>

        {submitError && (
          <ErrorState title="Création impossible" description={submitError} />
        )}

        {createdCommande && (
          <div className="rounded-sm border border-emerald-500/30 bg-emerald-500/5 p-5" role="status">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="font-semibold">Commande créée avec succès.</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {createdCommande.message || createdCommande.numero_commande}
                </p>
              </div>
              <StatusBadge status={createdCommande.statut} />
            </div>
            <Link
              to={`/commandes/${createdCommande.id}`}
              className="ops-focus mt-4 inline-flex rounded-sm bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Voir la commande {createdCommande.numero_commande}
            </Link>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-3">
          <Link
            to="/commandes"
            className="ops-focus rounded-sm border border-border bg-card px-4 py-2 text-sm font-semibold hover:bg-[hsl(var(--surface-raised))]"
          >
            Annuler
          </Link>
          <button
            type="submit"
            disabled={formUnavailable || submitting || Boolean(createdCommande)}
            className="ops-focus rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "Création..." : "Créer la commande"}
          </button>
        </div>
      </form>
    </div>
  );
}
